import { InfoOutlined } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import { llmClassifierVersionCreator } from "../../utils/llmClassifierVersionDisplay";

interface Props {
    beforeVersion: LLMClassifierImplementation;
    afterVersion: LLMClassifierImplementation;
    beforeLabel: string;
    afterLabel: string;
}

/** Shown when two versions compare identically. */
export function NoConfigChangesCard(props: Props) {
    const notes = [
        {
            side: "before",
            label: props.beforeLabel,
            version: props.beforeVersion,
        },
        { side: "after", label: props.afterLabel, version: props.afterVersion },
    ].filter((entry) => !!entry.version.versionDescription);

    return (
        <Box
            sx={(theme) => ({
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: 2,
                backgroundColor: "white.main",
                px: 2,
                py: 1.5,
            })}
        >
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
                <InfoOutlined fontSize="small" sx={{ color: "gray.600" }} />
                <Typography fontSize="15px" fontWeight={600}>
                    No model configuration changes
                </Typography>
            </Stack>

            <Typography color="gray.500" variant="body2">
                These two versions are identical.
            </Typography>

            {notes.length > 0 && (
                <Stack gap={0.5} mt={1.5}>
                    <Typography
                        color="gray.500"
                        variant="body2"
                        fontWeight={600}
                    >
                        {notes.length > 1
                            ? "Version descriptions"
                            : "Version description"}
                    </Typography>
                    {notes.map((entry) => (
                        <Typography
                            key={entry.side}
                            color="gray.700"
                            variant="body2"
                        >
                            <strong>{entry.label}</strong>:{" "}
                            {entry.version.versionDescription} ·{" "}
                            {llmClassifierVersionCreator(entry.version)}
                        </Typography>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
